import React, { memo, useCallback, useState } from 'react';
import { useIntl } from 'react-intl';
import {
	Badge,
	Box,
	Checkbox,
	Divider,
	FormControlLabel,
	IconButton,
	Popover,
	Tooltip,
	Typography
} from '@mui/material';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { ContentType, ContentTypeFilterProps } from 'models';

export const FILTER_TYPES: { type: ContentType; color: string; labelId: string; tooltipId: string }[] = [
	{ type: 'site-default', color: '#FFFFFF', labelId: 'FILTER_TYPE_SITE_DEFAULT', tooltipId: 'FILTER_TYPE_SITE_DEFAULT_TOOLTIP' },
	{ type: 'program', color: '#CE93D8', labelId: 'FILTER_TYPE_PROGRAM', tooltipId: 'FILTER_TYPE_PROGRAM_TOOLTIP' },
	{ type: 'custom', color: '#FFCC80', labelId: 'FILTER_TYPE_CUSTOM', tooltipId: 'FILTER_TYPE_CUSTOM_TOOLTIP' }
];

const ContentTypeFilter = ({ selectedTypes, onChange }: ContentTypeFilterProps) => {
	const intl = useIntl();
	const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
	const hasActiveFilters = selectedTypes.size > 0;

	const handleOpen = useCallback(
		(event: React.MouseEvent<HTMLElement>) => setAnchorEl(event.currentTarget),
		[]
	);
	const handleClose = useCallback(() => setAnchorEl(null), []);

	const handleToggle = useCallback(
		(type: ContentType) => {
			const next = new Set(selectedTypes);
			if (next.has(type)) next.delete(type);
			else next.add(type);
			onChange(next);
		},
		[selectedTypes, onChange]
	);

	const handleClear = useCallback(() => onChange(new Set()), [onChange]);

	return (
		<>
			<IconButton
				onClick={handleOpen}
				sx={{
					border: '1px solid',
					borderColor: 'primary.main',
					borderRadius: '4px'
				}}
			>
				<Badge
					color="primary"
					variant="dot"
					invisible={!hasActiveFilters}
					sx={{
						'& .MuiBadge-badge': {
							height: 12,
							minWidth: 12,
							borderRadius: '50%',
							top: -7,
							right: -7
						}
					}}
				>
					<FilterAltOutlinedIcon sx={{ color: 'primary.main' }} />
				</Badge>
			</IconButton>
			<Popover
				open={!!anchorEl}
				anchorEl={anchorEl}
				onClose={handleClose}
				anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
				transformOrigin={{ vertical: 'top', horizontal: 'right' }}
			>
				<Box sx={{ minWidth: 240, p: 1.5 }}>
					<Typography
						variant="overline"
						sx={{ fontWeight: 700, display: 'block', mb: 1 }}
					>
						{intl.formatMessage({ id: 'CONTENT_TYPE' })}
					</Typography>
					{FILTER_TYPES.map(({ type, color, labelId, tooltipId }) => (
						<Box key={type} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
							<FormControlLabel
								sx={{ mr: 0 }}
								control={
									<Checkbox
										checked={selectedTypes.has(type)}
										onChange={() => handleToggle(type)}
									/>
								}
								label={
									<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
										<InsertDriveFileIcon sx={{ color, fontSize: 18 }} />
										<Typography variant="body2">
											{intl.formatMessage({ id: labelId })}
										</Typography>
									</Box>
								}
							/>
							<Tooltip
								title={intl.formatMessage({ id: tooltipId })}
								placement="bottom"
								slotProps={{
									tooltip: {
										sx: {
											p: 2,
											color: '#FFFFFF',
											fontFamily: 'Roboto',
											fontSize: 14,
											fontWeight: 400,
											lineHeight: '18px'
										}
									}
								}}
							>
								<InfoOutlinedIcon sx={{ fontSize: 16, color: 'grey.500' }} />
							</Tooltip>
						</Box>
					))}
					<Divider sx={{ my: 1 }} />
					<Typography
						component="button"
						type="button"
						onClick={handleClear}
						disabled={!hasActiveFilters}
						sx={{
							background: 'none',
							border: 'none',
							p: 0,
							cursor: hasActiveFilters ? 'pointer' : 'default',
							color: hasActiveFilters ? 'primary.main' : 'grey.600',
							fontSize: 14
						}}
					>
						{intl.formatMessage({ id: 'CLEAR_FILTERS' })}
					</Typography>
				</Box>
			</Popover>
		</>
	);
};

export default memo(ContentTypeFilter);
